import { useEffect } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { FEATURE_QUERY_PARAM } from '@/lib/feature/feature.constants'
import {
  getStoredFeature,
  setStoredFeature,
  clearStoredFeature,
} from '@/lib/feature/feature.storage'
import { installFeatureFetchInterceptor } from '@/lib/feature/featureFetchInterceptor'
import { PATHS } from '@/lib/routes'
import { resolveClientFeature } from '@/lib/feature/resolveClientFeature'

/**
 * Routes where entering WITHOUT `?feature` is considered intentional.
 * On these routes, if `feature` is missing from the URL, sessionStorage
 * is cleared instead of restoring the feature.
 *
 * This prevents the feature from being injected when a user:
 * - Navigates directly to /login
 * - Opens /forgot-password from a bookmark
 */
const ENTRY_ROUTES: Array<string | RegExp> = [
  PATHS.login,
  '/forgot-password',
]

function isEntryRoute(pathname: string): boolean {
  return ENTRY_ROUTES.some(route =>
    typeof route === 'string'
      ? pathname === route
      : route.test(pathname),
  )
}

/**
 * The Service Worker keeps its own copy of the active feature (set via
 * postMessage, not sessionStorage) so it can rewrite fetch() URLs at the
 * network layer. It must be told explicitly whenever the feature changes —
 * including when it's cleared — or it keeps tagging requests with a stale
 * value forever, regardless of what the page's own URL/sessionStorage says.
 */
function notifyServiceWorker(feature: string | null): void {
  const controller = navigator.serviceWorker?.controller
  if (!controller) return
  controller.postMessage({ type: 'SET_FEATURE', feature })
}

/**
 * Keeps the `feature` query param synchronised between URL and sessionStorage.
 *
 * Sync rules:
 * - URL has `?feature=<value>` → save to sessionStorage (on any route)
 * - URL has `?feature=`        → explicit clear of sessionStorage
 * - URL has no `feature` param:
 *     - on entry routes (/login, /forgot-password) → clear sessionStorage
 *     - on app routes                              → restore from sessionStorage
 *
 * The restore uses navigate(..., { replace: true }), which re-triggers this effect.
 * On re-trigger the URL now has `feature`, hitting the "save" branch.
 * Maximum 2 executions per navigation.
 *
 * Mount in the root layout element of createBrowserRouter (needs useLocation).
 */
export function FeatureSync() {
  const navigate  = useNavigate()
  const location  = useLocation()
  const pathname  = location.pathname
  const [searchParams] = useSearchParams()

  // Install fetch interceptor for JS-initiated /assets/ requests.
  // In dev there is no K8s ingress to route around, and Vite's own internal
  // module URLs (/@react-refresh, /@vite/client, /src/main.tsx?import, etc.)
  // break if a stray ?feature= is appended — Vite serves index.html for any
  // path it doesn't recognise, which trips MIME-type-strict module loading.
  useEffect(() => {
    if (import.meta.env.DEV) return
    installFeatureFetchInterceptor()
  }, [])

  // Register the Service Worker that intercepts <link>/<script> asset loads.
  // The script URL itself includes ?feature=<value> so the ingress routes the
  // SW fetch to the same pod that owns this build. Without it, the request
  // for /feature-sw.js lands on the default pod and may be redirected, which
  // disqualifies the script for SW registration.
  //
  // In dev: skip registration AND actively unregister any leftover from a
  // previous Next.js session, otherwise it keeps intercepting Vite's module
  // requests and breaks HMR with "Expected a JavaScript-or-Wasm module".
  useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return

    if (import.meta.env.DEV) {
      navigator.serviceWorker.getRegistrations().then((regs) => {
        regs.forEach((reg) => {
          if (reg.active?.scriptURL.endsWith('feature-sw.js')) reg.unregister()
        })
      }).catch(() => { /* ignore */ })
      return
    }

    const feature = resolveClientFeature()
    const swUrl = feature
      ? `/feature-sw.js?feature=${encodeURIComponent(feature)}`
      : '/feature-sw.js'

    navigator.serviceWorker.register(swUrl, { scope: '/' }).then((reg) => {
      const controller = reg.active ?? navigator.serviceWorker.controller
      // Post even when feature is null — a freshly-registered SW otherwise
      // has no way to learn "no feature" if one was active in a previous tab.
      controller?.postMessage({ type: 'SET_FEATURE', feature })
    })
  }, [])

  useEffect(() => {
    const hasParam   = searchParams.has(FEATURE_QUERY_PARAM)
    const urlFeature = searchParams.get(FEATURE_QUERY_PARAM)

    // ── URL has `?feature` ──
    if (hasParam) {
      if (urlFeature) {
        setStoredFeature(urlFeature)
        notifyServiceWorker(urlFeature)
      } else {
        // ?feature= (empty) → explicit clear
        clearStoredFeature()
        notifyServiceWorker(null)
      }
      return
    }

    // ── URL has no `feature` param ──
    if (isEntryRoute(pathname)) {
      // Intentional entry without feature → clean slate
      clearStoredFeature()
      notifyServiceWorker(null)
      return
    }

    // App route without feature → try to restore from sessionStorage
    const stored = getStoredFeature()
    if (!stored) {
      notifyServiceWorker(null)
      return
    }

    const params = new URLSearchParams(searchParams.toString())
    params.set(FEATURE_QUERY_PARAM, stored)
    // Preserve location.state — some routes (e.g. movement/transaction detail)
    // read data passed via navigate(..., { state }) on the ORIGINAL navigation.
    // Without forwarding it here, this housekeeping replace wipes that state
    // and the destination view sees it as missing, e.g. redirecting away.
    navigate(`${pathname}?${params.toString()}`, { replace: true, state: location.state })
    // No notifyServiceWorker call here: the replace above re-triggers this
    // effect with `?feature=` now in the URL, which hits the branch above.
  }, [pathname, searchParams, navigate, location.state])

  return null
}
